// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Home peek. Clicking a Sorted row (or its Reply) opens that
// conversation in a small card over Home with the last few messages and a reply
// box; Expand goes to the real chat. The row order is frozen when the peek
// opens, so reading a chat (which marks it read and re-ranks the list) never
// moves Next somewhere unexpected.
//
// Closing a peek with something typed in its reply box (or leaving Home with
// one open) parks it: the corner's drafts bubble (DraftTray) keeps it on every
// page and opens it again, text and all, as a standalone peek.

import DraftTray from '@app/features/konvo_corner/state/DraftTray';
import type {HomePeekItem} from '@app/features/konvo_today/utils/HomePeekModel';
import {nextPeekKey, type PeekRect, stepPeekKey} from '@app/features/konvo_today/utils/HomePeekModel';
import {makeAutoObservable, observable} from 'mobx';

class HomePeekStore {
	key: string | null = null;
	/** The Sorted rows as they were when the peek opened. */
	order: Array<string> = [];
	/** The row the card lifts out of, in window coordinates. */
	anchor: PeekRect | null = null;
	/** Rows that were unread when the peek opened: Next visits these first. */
	waiting = new Set<string>();
	/** Rows opened in this peek session. */
	seen = new Set<string>();
	/** Rows answered from the peek: the footer says Sent. */
	sent = new Set<string>();
	/** What was typed per row, for sources without a draft store of their own. */
	drafts = new Map<string, string>();
	/** Files waiting in the reply box per row, for sources without an upload store of their own. */
	files = new Map<string, Array<{id: string; file: File}>>();
	/** Bumped to put the caret back in the reply box (Reply, Next). */
	focusTick = 0;
	/**
	 * A peek opened outside Home's list (an assistant source card, a brief's "Show me"):
	 * the item travels with it, there is no list to step through, and the app-wide host draws it.
	 */
	standalone: HomePeekItem | null = null;
	/** The item the open peek draws (set by its host), so closing it can park it. */
	showing: HomePeekItem | null = null;
	/** The host can park it: the desktop (the phone has no corner icon to keep it in). */
	private showingParkable = false;
	/** A channel's draft lives in the chat's own draft store; ChannelPeek says how to read it. */
	private channelDraft: (channelId: string) => string = () => '';
	/** Peeks closed with a draft in them, newest last. */
	parked: Array<HomePeekItem> = [];
	/** Expand took the draft to the real conversation: closing does not park it. */
	private expanding: string | null = null;

	constructor() {
		makeAutoObservable<this, 'expanding' | 'showingParkable' | 'channelDraft'>(
			this,
			{
				standalone: observable.ref,
				showing: observable.ref,
				parked: observable.shallow,
				expanding: false,
				showingParkable: false,
				channelDraft: false,
			},
			{autoBind: true},
		);
	}

	get isOpen(): boolean {
		return this.key != null;
	}

	open(key: string, params: {order: Array<string>; waiting: Array<string>; anchor: PeekRect | null}): void {
		this.order = params.order.includes(key) ? params.order : [key, ...params.order];
		this.waiting = new Set(params.waiting);
		this.seen = new Set([key]);
		this.sent = new Set();
		this.anchor = params.anchor;
		this.standalone = null;
		this.unpark(key);
		this.key = key;
		this.focusTick += 1;
	}

	/** One conversation on its own, lifted out of `anchor` (the card that was clicked) when there is one. */
	openStandalone(item: HomePeekItem, anchor: PeekRect | null = null): void {
		this.open(item.key, {order: [item.key], waiting: [], anchor});
		this.standalone = item;
	}

	close(): void {
		const item = this.showing;
		if (item && item.key === this.key && this.showingParkable && this.expanding !== item.key && this.hasDraft(item))
			this.park(item);
		this.expanding = null;
		this.showing = null;
		this.key = null;
		this.anchor = null;
		this.standalone = null;
	}

	/** The host draws this item now; `parkable` where the corner can keep it (desktop). */
	show(item: HomePeekItem | null, parkable: boolean): void {
		this.showing = item;
		this.showingParkable = parkable;
	}

	setChannelDraftReader(read: (channelId: string) => string): void {
		this.channelDraft = read;
	}

	/** What was typed, wherever the source keeps it. */
	draftText(item: HomePeekItem): string {
		const text = this.draft(item.key).trim();
		if (text || item.target.kind !== 'channel') return text;
		return this.channelDraft(item.target.channelId).trim();
	}

	/** Expand is taking the open draft to the real conversation. */
	markExpanding(): void {
		this.expanding = this.key;
	}

	/** Something typed or attached that has not been sent. */
	hasDraft(item: HomePeekItem): boolean {
		return this.draftText(item).length > 0 || this.filesFor(item.key).length > 0;
	}

	/** Keep it in the corner's drafts bubble. */
	park(item: HomePeekItem): void {
		this.parked = [...this.parked.filter((entry) => entry.key !== item.key), item];
	}

	/** Parked peeks that still hold a draft (one sent from its real chat drops out). */
	get parkedDrafts(): Array<HomePeekItem> {
		return this.parked.filter((item) => this.hasDraft(item));
	}

	unpark(key: string): void {
		this.parked = this.parked.filter((entry) => entry.key !== key);
	}

	/** Open a parked draft again, on whatever page this is. */
	resume(key: string): void {
		const item = this.parked.find((entry) => entry.key === key);
		if (!item) return;
		this.unpark(key);
		if (this.key) this.close();
		this.openStandalone(item);
	}

	go(key: string): void {
		this.unpark(key);
		this.seen.add(key);
		this.key = key;
		this.focusTick += 1;
	}

	get nextKey(): string | null {
		return this.key ? nextPeekKey(this.order, this.key, this.waiting, this.seen) : null;
	}

	get position(): {index: number; total: number} {
		return {index: this.key ? this.order.indexOf(this.key) : -1, total: this.order.length};
	}

	next(): void {
		const key = this.nextKey;
		if (key) this.go(key);
		else this.close();
	}

	step(delta: 1 | -1): void {
		const key = this.key ? stepPeekKey(this.order, this.key, delta) : null;
		if (key) this.go(key);
	}

	/** The row is gone from the list (Done): drop it from the order and move on. */
	forget(key: string): void {
		const following = this.key === key ? this.nextKey : this.key;
		this.order = this.order.filter((entry) => entry !== key);
		if (this.key !== key) return;
		if (following) this.go(following);
		else this.close();
	}

	markSent(key: string): void {
		this.sent.add(key);
	}

	draft(key: string): string {
		return this.drafts.get(key) ?? '';
	}

	filesFor(key: string): Array<{id: string; file: File}> {
		return this.files.get(key) ?? [];
	}

	addFiles(key: string, files: Array<File>): void {
		const added = files.map((file) => ({id: `${Date.now()}:${Math.random().toString(36).slice(2)}`, file}));
		this.files.set(key, [...this.filesFor(key), ...added]);
	}

	removeFile(key: string, id: string): void {
		const next = this.filesFor(key).filter((entry) => entry.id !== id);
		if (next.length > 0) this.files.set(key, next);
		else this.files.delete(key);
	}

	clearFiles(key: string): void {
		this.files.delete(key);
	}

	setDraft(key: string, text: string): void {
		if (text) this.drafts.set(key, text);
		else this.drafts.delete(key);
	}
}

const HomePeek = new HomePeekStore();

DraftTray.register(() =>
	HomePeek.parkedDrafts.map((item) => ({
		key: `peek:${item.key}`,
		title: () => item.name,
		preview: HomePeek.draftText(item),
		avatar: () => item.avatar(32),
		badge: () => item.badge,
		open: () => HomePeek.resume(item.key),
		dismiss: () => HomePeek.unpark(item.key),
	})),
);

export default HomePeek;
